import { buildSupportedLinkPreview } from '../components/molecules/LinkWithPreview/supportedLinkPreview.ts';

const URL_SPLIT_REGEX = /(https?:\/\/[^\s]+)/g;
const URL_ONLY_REGEX = /^https?:\/\/[^\s]+$/i;
const BRACKET_PAIRS = {
    ')': '(',
    ']': '[',
    '}': '{',
};
const TRAILING_SENTENCE_PUNCTUATION_REGEX = /[.,!?]/;

const countCharacter = (value, target) => Array.from(value).reduce(
    (count, character) => (character === target ? count + 1 : count),
    0,
);

const hasUnmatchedTrailingBracket = (value, character) => {
    const openingCharacter = BRACKET_PAIRS[character];

    if (!openingCharacter) {
        return false;
    }

    return countCharacter(value, character) > countCharacter(value, openingCharacter);
};

const endsWithUnmatchedTrailingBracket = (value) => {
    if (!value) {
        return false;
    }

    const trailingCharacter = value[value.length - 1];
    return hasUnmatchedTrailingBracket(value, trailingCharacter);
};

const tryParseUrl = (value) => {
    try {
        return new URL(value);
    } catch (error) {
        return null;
    }
};

const getUrlTail = (value) => {
    const parsedUrl = tryParseUrl(value);

    if (!parsedUrl) {
        return '';
    }

    return `${parsedUrl.pathname || ''}${parsedUrl.search || ''}${parsedUrl.hash || ''}`;
};

const isHostOnlyUrl = (value) => {
    const parsedUrl = tryParseUrl(value);

    if (!parsedUrl) {
        return false;
    }

    return !parsedUrl.search
        && !parsedUrl.hash
        && (!parsedUrl.pathname || parsedUrl.pathname === '/');
};

const endsWithNumericLookupToken = (value) => /(?:^|[/?#=&])(?:id)?\d+$/i.test(getUrlTail(value));

const resolvesToSupportedPreviewUrl = (value) => Boolean(buildSupportedLinkPreview(value));

const shouldStripTrailingSentencePunctuation = (value) => {
    if (!value) {
        return false;
    }

    const trailingCharacter = value[value.length - 1];

    if (!TRAILING_SENTENCE_PUNCTUATION_REGEX.test(trailingCharacter)) {
        return false;
    }

    const candidateUrl = value.slice(0, -1);

    if (!tryParseUrl(candidateUrl)) {
        return false;
    }

    return endsWithUnmatchedTrailingBracket(candidateUrl)
        || isHostOnlyUrl(value)
        || endsWithNumericLookupToken(candidateUrl)
        || resolvesToSupportedPreviewUrl(candidateUrl);
};

const previousTextEndsWithMatchingOpeningBracket = (value, closingCharacter) => {
    const openingCharacter = BRACKET_PAIRS[closingCharacter];

    if (!openingCharacter) {
        return 0;
    }

    const normalizedValue = String(value || '');
    let count = 0;

    for (let index = normalizedValue.length - 1; index >= 0; index -= 1) {
        if (normalizedValue[index] !== openingCharacter) {
            break;
        }

        count += 1;
    }

    return count;
};

const shouldStripTrailingClosingBracket = (value, previousText, strippedSentencePunctuation, strippedWrapperBrackets) => {
    if (!value) {
        return false;
    }

    const trailingCharacter = value[value.length - 1];

    if (!hasUnmatchedTrailingBracket(value, trailingCharacter)) {
        return false;
    }

    const wrapperBudget = previousTextEndsWithMatchingOpeningBracket(previousText, trailingCharacter);
    const wrapperStripsUsed = strippedWrapperBrackets[trailingCharacter] || 0;

    if (wrapperStripsUsed < wrapperBudget) {
        return true;
    }

    if (!strippedSentencePunctuation) {
        return shouldStripTrailingSentencePunctuation(value.slice(0, -1));
    }

    return wrapperBudget === 0
        && countCharacter(value, BRACKET_PAIRS[trailingCharacter]) > 0
        && !hasUnmatchedTrailingBracket(value.slice(0, -1), trailingCharacter);
};

export const splitTrailingPunctuation = (value, previousText = '') => {
    const normalizedValue = String(value || '');
    let sanitizedUrl = normalizedValue;
    let strippedSentencePunctuation = false;
    const strippedWrapperBrackets = {};

    while (sanitizedUrl) {
        if (shouldStripTrailingSentencePunctuation(sanitizedUrl)) {
            sanitizedUrl = sanitizedUrl.slice(0, -1);
            strippedSentencePunctuation = true;
            continue;
        }

        if (shouldStripTrailingClosingBracket(
            sanitizedUrl,
            previousText,
            strippedSentencePunctuation,
            strippedWrapperBrackets,
        )) {
            const strippedCharacter = sanitizedUrl[sanitizedUrl.length - 1];
            sanitizedUrl = sanitizedUrl.slice(0, -1);
            strippedWrapperBrackets[strippedCharacter] = (strippedWrapperBrackets[strippedCharacter] || 0) + 1;
            continue;
        }

        break;
    }

    return {
        trailingText: normalizedValue.slice(sanitizedUrl.length),
        url: sanitizedUrl,
    };
};

export const splitTextIntoLinkParts = (content) => {
    if (!content || typeof content !== 'string') {
        return [];
    }

    const parts = content.split(URL_SPLIT_REGEX);

    return parts.map((part, index) => {
        if (!URL_ONLY_REGEX.test(part)) {
            return {
                type: 'text',
                value: part,
            };
        }

        const previousPart = index > 0 ? parts[index - 1] : '';
        const { url, trailingText } = splitTrailingPunctuation(part, previousPart);

        return {
            type: 'url',
            original: part,
            trailingText,
            url,
        };
    });
};

const appendSupportedPreviewUrl = (candidateUrl, previewUrls, seenPreviewUrls) => {
    const parsedUrl = tryParseUrl(candidateUrl);
    const normalizedUrl = parsedUrl ? parsedUrl.href : null;

    if (!normalizedUrl || !resolvesToSupportedPreviewUrl(normalizedUrl) || seenPreviewUrls.has(normalizedUrl)) {
        return;
    }

    seenPreviewUrls.add(normalizedUrl);
    previewUrls.push(normalizedUrl);
};

const collectSupportedPreviewUrlsFromHtmlNode = (node, previewUrls, seenPreviewUrls) => {
    if (!node || typeof Node === 'undefined') {
        return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
        splitTextIntoLinkParts(node.nodeValue || '').forEach((part) => {
            if (part.type === 'url' && part.url) {
                appendSupportedPreviewUrl(part.url, previewUrls, seenPreviewUrls);
            }
        });
        return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
    }

    if (node.tagName.toLowerCase() === 'a') {
        appendSupportedPreviewUrl(node.getAttribute('href'), previewUrls, seenPreviewUrls);
    }

    Array.from(node.childNodes || []).forEach((childNode) => {
        collectSupportedPreviewUrlsFromHtmlNode(childNode, previewUrls, seenPreviewUrls);
    });
};

export const extractSupportedPreviewUrlsFromHtml = (html) => {
    if (!html || typeof html !== 'string' || typeof DOMParser === 'undefined') {
        return [];
    }

    try {
        const documentNode = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
        const rootNode = documentNode.body.firstChild || documentNode.body;
        const previewUrls = [];
        const seenPreviewUrls = new Set();

        collectSupportedPreviewUrlsFromHtmlNode(rootNode, previewUrls, seenPreviewUrls);

        return previewUrls;
    } catch (error) {
        return [];
    }
};

export const extractPrimaryLinkFromText = (content) => {
    for (const part of splitTextIntoLinkParts(content)) {
        if (part.type !== 'url' || !part.url) {
            continue;
        }

        const parsedUrl = tryParseUrl(part.url);
        return parsedUrl ? parsedUrl.href : part.url;
    }

    return null;
};

export {
    URL_ONLY_REGEX,
    URL_SPLIT_REGEX,
};
